import { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Moment from 'moment';
import Swal from 'sweetalert2';
import { saveAs } from 'file-saver';
import styled from 'styled-components';
import {
  getMeetingByTypeCodeWithAgendas,
  downloadAgendaFile,
} from '../services/SETEMeetingAPI';
import { MeetingWithAgendasModel } from '../models/set-e-meeting/MeetingWithAgendasModel';
import { AgendaAccessModel } from '../models/set-e-meeting/AgendaAccessModel';
import { AgendaFileModel } from '../models/set-e-meeting/AgendaFileModel';
import SetLogo from '../images/logo/logo-set.svg';
import TsdLogo from '../images/logo/logo-tsd.svg';
import TchLogo from '../images/logo/logo-tch.svg';
import TfexLogo from '../images/logo/logo-tfex.svg';
import SvhLogo from '../images/logo/logo-svh.svg';
import TnvdrLogo from '../images/logo/logo-tnvdr.svg';
import LiveLogo from '../images/logo/logo-live.svg';
import FinnetLogo from '../images/logo/logo-finnet.svg';
import TdxLogo from '../images/logo/logo-tdx.svg';
import DapLogo from '../images/logo/logo-dap.svg';
import SettradeLogo from '../images/logo/logo-settrade.svg';

export const MouseHover = styled.div`
  cursor: pointer;

  :hover {
    opacity: 50%;
  }
`;

const AdminAgenda = (props: Props) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [meeting, setMeeting] = useState<MeetingWithAgendasModel | null>();

  useEffect(() => {
    const meetingTypeCode = queryParams.get('meetingType');
    const executive = queryParams.get('executive');
    if (!meetingTypeCode) return;

    const isExclusive = executive === 'true';
    getMeetingByTypeCodeWithAgendas(meetingTypeCode, isExclusive)
      .then((res) => setMeeting(res.data))
      .catch(() => setMeeting(null));
  }, [queryParams.get('meetingType'), queryParams.get('executive')]);

  const getLatestFile = (
    files: AgendaFileModel[]
  ): AgendaFileModel | undefined => {
    return files.reduce<AgendaFileModel | undefined>((latest, file) => {
      if (!latest) return file;
      if (file.version !== latest.version) {
        return file.version > latest.version ? file : latest;
      }
      return file.file_id > latest.file_id ? file : latest;
    }, undefined);
  };

  const getDownloadableFiles = (
    agenda: AgendaAccessModel
  ): AgendaFileModel[] => {
    const powerpoint = getLatestFile(
      (agenda.files ?? []).filter((file) => file.file_category === 'POWERPOINT')
    );
    const file = powerpoint ?? getLatestFile(agenda.presentation_file ?? []);
    return file ? [file] : [];
  };

  const handleOpenDocument = (file: AgendaFileModel) => {
    downloadAgendaFile(file.file_id)
      .then((res) => {
        if (res.status === 200) {
          saveAs(res.data, file.original_filename);
        } else {
          alert('เกิดข้อผิดพลาดขณะดาวน์โหลดเอกสาร กรุณาลองใหม่อีกครั้ง', true);
        }
      })
      .catch(() => {
        alert('เกิดข้อผิดพลาดขณะดาวน์โหลดเอกสาร กรุณาลองใหม่อีกครั้ง', true);
      });
  };

  const alert = (msg: string, isError: Boolean) => {
    return Swal.fire({
      text: msg,
      title: isError ? 'ข้อผิดพลาด' : 'คำเตือน',
      icon: isError ? 'error' : 'warning',
      confirmButtonText: 'Close',
      customClass: {
        confirmButton: 'btn btn-secondary',
      },
      buttonsStyling: false,
    });
  };

  const getLogo = (meetingType: string | undefined): string | undefined => {
    switch (meetingType) {
      case 'SET':
        return `${SetLogo}`;
      case 'TSD':
        return `${TsdLogo}`;
      case 'TCH':
        return `${TchLogo}`;
      case 'TFEX':
        return `${TfexLogo}`;
      case 'TNVDR':
        return `${TnvdrLogo}`;
      case 'SVH':
        return `${SvhLogo}`;
      case 'STT':
        return `${SettradeLogo}`;
      case 'LIVE':
        return `${LiveLogo}`;
      case 'FINNET':
        return `${FinnetLogo}`;
      case 'TDX':
        return `${TdxLogo}`;
      case 'DAP':
        return `${DapLogo}`;
      default:
        return undefined;
    }
  };

  const replaceSpace = (lb: string) => {
    return lb.replace(/ /g, '&nbsp;');
  };

  const thaiMonths = [
    'มกราคม',
    'กุมภาพันธ์',
    'มีนาคม',
    'เมษายน',
    'พฤษภาคม',
    'มิถุนายน',
    'กรกฎาคม',
    'สิงหาคม',
    'กันยายน',
    'ตุลาคม',
    'พฤศจิกายน',
    'ธันวาคม',
  ];

  const formatDate = (value: string | undefined): string => {
    if (!value) return '';
    const date = Moment(value);
    return `${date.date()} ${thaiMonths[date.month()]} ${date.year() + 543}`;
  };

  const formatTime = (value: string | undefined): string => {
    return value ? Moment(value).format('HH:mm') : '';
  };

  const specialMeetingTypes = ['TDX', 'DAP', 'FINNET', 'LIVE'];
  const meetingTypeCode = meeting?.meeting_type_code ?? '';
  const hasSpecialMeetingType = specialMeetingTypes.includes(meetingTypeCode);
  const navClassName = 'row align-items-center text-center';
  const headerClass = hasSpecialMeetingType
    ? `${navClassName} nav-border-header`
    : `${navClassName} nav-header`;

  return (
    <>
      <div className="container">
        <div className={headerClass}>
          <div className="my-3">
            {getLogo(meetingTypeCode) !== undefined ? (
              <div className="row my-1 justify-content-center">
                <div className="col-md-auto">
                  <img
                    src={getLogo(meetingTypeCode)}
                    style={{ maxWidth: '9rem', maxHeight: 'fit-content' }}
                    alt={meetingTypeCode}
                  />
                </div>
              </div>
            ) : (
              <></>
            )}
            <div className="row py-1">
              <h4 className="col-12 d-flex justify-content-center mb-0">
                {meeting?.subject}
              </h4>
            </div>
            <div className="row py-1 justify-content-center">
              <h5 className="mb-0">
                {meeting?.meeting_no ? `ครั้งที่ ${meeting?.meeting_no}` : ''}{' '}
                {formatDate(meeting?.time_start)}
              </h5>
            </div>
            <div className="row py-1 justify-content-center">
              <div className="col">
                <h5 className="col-md-auto mb-0 align-self-center">
                  {meeting?.time_start
                    ? `เวลา ${formatTime(meeting?.time_start)}`
                    : ''}
                  &nbsp;
                  {meeting?.time_end
                    ? `ถึง ${formatTime(meeting?.time_end)} น.`
                    : ''}
                </h5>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <table className="table">
            {meeting?.agendas
              ?.filter((group) => group.length > 0)
              .map((group, groupIndex) => (
                <tbody key={groupIndex} style={{ borderTop: 'none' }}>
                  <tr
                    style={{
                      backgroundColor: 'antiquewhite',
                    }}
                  >
                    <td colSpan={3}>
                      <div style={{ marginLeft: '2%' }}>
                        <h5 className="mb-0">{group[0]?.agenda_type_code}</h5>
                      </div>
                    </td>
                  </tr>
                  {group.map((item) => {
                    const files = getDownloadableFiles(item);
                    const isSendLater =
                      item.presentation_mode === 'send_later' &&
                      files.length === 0;
                    return (
                      <tr key={item.id} className="align-middle">
                        <td width={'4%'}></td>
                        <td>
                          <label
                            className="text-black py-2 pb-0"
                            dangerouslySetInnerHTML={{
                              __html: replaceSpace(item.name),
                            }}
                          ></label>
                        </td>
                        <td className="text-end" width={'10%'}>
                          {files.length > 0 ? (
                            files.map((file) => (
                              <Fragment key={file.file_id}>
                                <MouseHover>
                                  <button
                                    style={{ backgroundColor: 'orange' }}
                                    className="btn btn-warning text-white text-bold"
                                    onClick={() => handleOpenDocument(file)}
                                  >
                                    เปิด
                                  </button>
                                </MouseHover>
                              </Fragment>
                            ))
                          ) : isSendLater ? (
                            <div className="text-secondary">นำส่งภายหลัง</div>
                          ) : (
                            <></>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
          </table>
        </div>
      </div>
    </>
  );
};

type Props = {
  admin: boolean;
  boardAdmin?: boolean;
};

export default AdminAgenda;
